import { useCallback, useMemo, useRef, useState } from "react";
import type {
  ParentToIframeMessage,
  UrlChangeMessage,
} from "../../../shared/liveBrowserRuntime/protocol";
import type { LiveFeature } from "./feature";
function appendBranch(path: string, branchId: string): string {
  const u = new URL(path, "http://x");
  u.searchParams.set("branch", branchId);
  return u.pathname + u.search + u.hash;
}

// Seed the display path with the branch the reporter injects, so the URL bar
// doesn't briefly show the un-branched route before the iframe reports back.
function initialNavPath(
  baseRoute: string | undefined,
  branchId: string | undefined
): string {
  if (baseRoute && branchId) return appendBranch(baseRoute, branchId);
  return baseRoute || "/";
}

export type Navigation = {
  feature: LiveFeature;
  path: string;
  canBack: boolean;
  canForward: boolean;
  back: () => void;
  forward: () => void;
  reload: () => void;
};

export function useNavigation(
  // Seeds the displayed URL before the iframe reports its own path back.
  baseRoute?: string,
  branchId?: string
): Navigation {
  const [path, setPath] = useState<string>(() =>
    initialNavPath(baseRoute, branchId)
  );
  const [history, setHistory] = useState<{
    entries: string[];
    index: number;
  }>({ entries: [], index: -1 });

  const intentRef = useRef<"back" | "forward" | null>(null);
  const initTimeRef = useRef<number>(0);
  const portRef = useRef<MessagePort | null>(null);

  const handleEvent = useCallback(
    (newPath: string, kind: UrlChangeMessage["kind"]) => {
      setPath(newPath);
      if (kind === "init") initTimeRef.current = Date.now();
      // Routers often pushState during mount (trailing-slash, basename
      // normalization); swallow those so back doesn't light up unprompted.
      const inInitWindow =
        initTimeRef.current > 0 && Date.now() - initTimeRef.current < 300;
      setHistory((prev) => {
        const intent = intentRef.current;
        intentRef.current = null;
        if (intent === "back") {
          return { ...prev, index: Math.max(0, prev.index - 1) };
        }
        if (intent === "forward") {
          return {
            ...prev,
            index: Math.min(prev.entries.length - 1, prev.index + 1),
          };
        }
        // A fresh `init` from a document that isn't the one we already show is
        // a full-page navigation (server-side routing) — push it so back /
        // forward work. A client-side SPA only ever emits one `init`, so this
        // never fires for it; a reload of the same path is a no-op.
        if (kind === "init") {
          if (prev.index < 0) return { entries: [newPath], index: 0 };
          if (prev.entries[prev.index] === newPath) return prev;
          const truncated = prev.entries.slice(0, prev.index + 1);
          return { entries: [...truncated, newPath], index: truncated.length };
        }
        if (kind === "replace" || inInitWindow) {
          if (prev.index < 0) return { entries: [newPath], index: 0 };
          const entries = [...prev.entries];
          entries[prev.index] = newPath;
          return { ...prev, entries };
        }
        if (prev.entries[prev.index] === newPath) return prev;
        const truncated = prev.entries.slice(0, prev.index + 1);
        return { entries: [...truncated, newPath], index: truncated.length };
      });
    },
    []
  );

  const feature = useMemo<LiveFeature>(
    () => ({
      attach: (api) => {
        const port = api.port;
        portRef.current = port;
        if (!port) return;

        const send = (message: ParentToIframeMessage) => {
          try {
            port.postMessage(message);
          } catch {}
        };

        // The runtime's urlReporter keeps this branch on outgoing
        // pushState/replaceState URLs; send it before the page's own scripts
        // touch history.
        send({ type: "3b-nav-config", branchId });

        const onMessage = (event: MessageEvent) => {
          const data = event.data as UrlChangeMessage | undefined;
          if (!data || data.type !== "3b-vb-url") return;
          if (typeof data.path !== "string") return;
          handleEvent(data.path, data.kind);
        };
        port.addEventListener("message", onMessage);
        port.start();
        return () => {
          port.removeEventListener("message", onMessage);
        };
      },
    }),
    [branchId, handleEvent]
  );

  const back = useCallback(() => {
    if (history.index <= 0) return;
    intentRef.current = "back";
    try {
      portRef.current?.postMessage({ type: "3b-nav-back" });
    } catch {}
  }, [history.index]);

  const forward = useCallback(() => {
    if (history.index >= history.entries.length - 1) return;
    intentRef.current = "forward";
    try {
      portRef.current?.postMessage({ type: "3b-nav-forward" });
    } catch {}
  }, [history.index, history.entries.length]);

  // Reload the frame's current document in place. The fresh `init` reports
  // the same path, so history and the URL bar are unchanged — unlike a
  // remount, which would rebuild the iframe at the step's root.
  const reload = useCallback(() => {
    try {
      portRef.current?.postMessage({ type: "3b-nav-reload" });
    } catch {}
  }, []);

  return {
    feature,
    path,
    canBack: history.index > 0,
    canForward: history.index < history.entries.length - 1,
    back,
    forward,
    reload,
  };
}
